import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { setUser } from "../../redux/slices/userSlice";
import {
  confirmEmailChange,
  sendEmailChangeCode,
} from "../../services/backend/EntityAuthService";

// Cambiar el correo de acceso de una cuenta de empresa.
//
// Para estas cuentas el correo no es un dato de contacto más: es la credencial
// —no tienen contraseña, entran con un código que les llega ahí—, así que
// cambiarlo toca Firebase Auth además de la ficha. De ahí que sea un flujo
// propio y no un campo editable en el perfil.
//
// El código va al correo NUEVO, no al actual: lo que hay que probar es que la
// dirección nueva existe y es suya. Que la actual lo sea ya lo prueba el hecho
// de tener la sesión abierta.

export default function ChangeEmailDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const reset = () => {
    setEmail(""); setCode(""); setSent(false); setError(null); setInfo(null);
  };
  const close = () => { reset(); onClose(); };

  const send = async () => {
    setBusy(true); setError(null);
    const result = await sendEmailChangeCode(email.trim(), user);
    setBusy(false);
    if (!result.ok) { setError(result.error); return; }
    setSent(true);
    setInfo(`Enviamos un código a ${email.trim()}. Vence en ${result.expiresInMinutes || 10} minutos.`);
  };

  const confirm = async () => {
    setBusy(true); setError(null);
    const result = await confirmEmailChange({ newEmail: email.trim(), code: code.trim(), user });
    setBusy(false);
    if (!result.ok) { setError(result.error); return; }
    // El documento en Redux queda con el correo viejo si no se actualiza aquí, y
    // es el que pinta el perfil y la cabecera.
    dispatch(setUser({ ...user, email: result.email }));
    close();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="xs">
      <DialogTitle>Cambiar mi correo</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Hoy entras con <strong>{user?.email}</strong>. Escribe el correo nuevo y te
            enviaremos un código para confirmarlo; desde ese momento entras con el nuevo.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          {!error && info && <Alert severity="info">{info}</Alert>}

          <TextField
            fullWidth type="email" label="Correo nuevo" value={email}
            disabled={sent || busy} autoFocus
            onChange={(e) => setEmail(e.target.value)}
          />

          {sent && (
            <TextField
              fullWidth label="Código" value={code} autoFocus
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancelar</Button>
        {sent && (
          <Button onClick={send} disabled={busy} sx={{ textTransform: "none" }}>Reenviar</Button>
        )}
        <Button
          variant="contained"
          onClick={sent ? confirm : send}
          disabled={busy || (sent ? code.length !== 6 : !email.trim())}
          sx={{ textTransform: "none" }}
        >
          {busy ? <CircularProgress size={22} /> : sent ? "Confirmar" : "Enviarme el código"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
